import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { allAPiServicesCall } from "../services/apiServices";
import { getAuthConfig } from "../services/apiUtils";
import {
  buildPageTargetPayload,
  getTargetDisplayLabel,
  getTargetKey,
  normalizeGenreValue,
} from "../utils/pageTarget";

export default function usePageTargetSelection() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [targets, setTargets] = useState([]);
  const [selectedTargetKey, setSelectedTargetKey] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [isLoadingTargets, setIsLoadingTargets] = useState(true);
  const [targetError, setTargetError] = useState("");

  useEffect(() => {
    let active = true;

    const loadTargets = async () => {
      setIsLoadingTargets(true);
      setTargetError("");

      try {
        const response = await allAPiServicesCall.getPageConfigTargets(
          getAuthConfig(),
          navigate
        );
        if (!active) return;

        const nextTargets = Array.isArray(response?.data) ? response.data : [];
        setTargets(nextTargets);
      } catch (error) {
        if (!active) return;
        setTargetError("Failed to load page targets");
      } finally {
        if (active) {
          setIsLoadingTargets(false);
        }
      }
    };

    loadTargets();

    return () => {
      active = false;
    };
  }, [navigate]);

  const filteredTargets = useMemo(() => {
    const query = targetSearch.trim().toLowerCase();
    const nextTargets = !query
      ? targets
      : targets.filter((target) =>
          [target.label, target.genreSlug, target.pageType]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query))
        );

    if (!selectedTargetKey) {
      return nextTargets;
    }

    const hasSelectedTarget = nextTargets.some(
      (target) => getTargetKey(target) === selectedTargetKey
    );

    if (hasSelectedTarget) {
      return nextTargets;
    }

    const selectedTarget = targets.find(
      (target) => getTargetKey(target) === selectedTargetKey
    );

    if (!selectedTarget) {
      return nextTargets;
    }

    return [selectedTarget, ...nextTargets];
  }, [selectedTargetKey, targetSearch, targets]);

  const selectedTarget = useMemo(
    () => targets.find((target) => getTargetKey(target) === selectedTargetKey) || null,
    [selectedTargetKey, targets]
  );

  useEffect(() => {
    if (!targets.length) return;

    const pageTypeParam = searchParams.get("pageType") || "homepage";
    const genreSlugParam = normalizeGenreValue(searchParams.get("genreSlug"));

    const matchedTarget =
      targets.find((target) => {
        if (target.pageType !== pageTypeParam) return false;
        if (pageTypeParam === "genre") {
          return normalizeGenreValue(target.genreSlug) === genreSlugParam;
        }
        return true;
      }) ||
      targets.find((target) => target.pageType === "homepage") ||
      targets[0];

    const nextTargetKey = getTargetKey(matchedTarget);

    if (nextTargetKey !== selectedTargetKey) {
      setSelectedTargetKey(nextTargetKey);
    }
  }, [searchParams, selectedTargetKey, targets]);

  const selectTarget = useCallback(
    (nextTargetKey) => {
      setSelectedTargetKey(nextTargetKey);

      const nextTarget = targets.find(
        (target) => getTargetKey(target) === nextTargetKey
      );
      if (!nextTarget) return;

      const nextPageType = nextTarget.pageType || "homepage";
      const nextGenreSlug =
        nextPageType === "genre"
          ? normalizeGenreValue(nextTarget.genreSlug)
          : "";

      const currentPageType = searchParams.get("pageType") || "homepage";
      const currentGenreSlug = normalizeGenreValue(searchParams.get("genreSlug"));

      if (
        currentPageType === nextPageType &&
        currentGenreSlug === nextGenreSlug
      ) {
        return;
      }

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("pageType", nextPageType);
      if (nextPageType === "genre" && nextGenreSlug) {
        nextParams.set("genreSlug", nextGenreSlug);
      } else {
        nextParams.delete("genreSlug");
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams, targets]
  );

  useEffect(() => {
    const query = targetSearch.trim().toLowerCase();
    if (!query || !targets.length) return;

    const matches = targets.filter((target) =>
      [target.label, target.genreSlug, target.pageType]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );

    if (!matches.length) return;

    const exactMatch =
      matches.find(
        (target) => normalizeGenreValue(target.genreSlug) === normalizeGenreValue(query)
      ) ||
      matches.find((target) => String(target.label || "").toLowerCase() === query) ||
      matches.find(
        (target) => target.pageType === "homepage" && "homepage".startsWith(query)
      );

    const nextTarget = exactMatch || (matches.length === 1 ? matches[0] : null);
    if (!nextTarget) return;

    const nextTargetKey = getTargetKey(nextTarget);
    if (nextTargetKey === selectedTargetKey) return;

    selectTarget(nextTargetKey);
  }, [selectedTargetKey, selectTarget, targetSearch, targets]);

  return {
    targets,
    filteredTargets,
    selectedTarget,
    selectedTargetKey,
    setSelectedTargetKey: selectTarget,
    selectedTargetLabel: getTargetDisplayLabel(selectedTarget),
    selectedTargetParams: buildPageTargetPayload(selectedTarget),
    targetSearch,
    setTargetSearch,
    isLoadingTargets,
    targetError,
  };
}
